import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Input,
  Text,
  VStack,
  Image
} from '@chakra-ui/react';

import { authService } from '../services/authService';
import { isStrongPassword, PASSWORD_RULES } from '../services/passwordPolicy';

export const ChangePasswordPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  // Motivo vindo do login (428): primeiro acesso/senha redefinida ou senha vencida
  const reason = (location.state as { reason?: string } | null)?.reason;
  
  // Verificar se há username na query string
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const usernameParam = params.get('username');
    if (usernameParam) {
      setUsername(usernameParam);
    }
  }, [location]);

  // Validação de campos
  const validateForm = () => {
    if (!username) {
      setError('Por favor, informe seu nome de usuário');
      return false;
    }
    if (!currentPassword) {
      setError('Por favor, informe sua senha atual');
      return false;
    }
    if (!newPassword) {
      setError('Por favor, informe sua nova senha');
      return false;
    }
    if (!confirmPassword) {
      setError('Por favor, confirme sua nova senha');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    if (newPassword === currentPassword) {
      setError('A nova senha deve ser diferente da senha atual');
      return false;
    }
    if (!isStrongPassword(newPassword, username)) {
      setError(PASSWORD_RULES);
      return false;
    }
    
    return true;
  };

  const handleChangePassword = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      setError(null);
      
      await authService.changePassword(username, currentPassword, newPassword);
      
      // Exibir mensagem de sucesso
      setError(null);
      
      // Redirecionar para a página de login após 2 segundos
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (err: any) {
      console.error('Erro ao alterar senha:', err);
      setError(err.message || 'Não foi possível alterar a senha. Verifique se a senha atual está correta.');
    } finally {
      setIsLoading(false);
    }
  };

  // Componente para mostrar/ocultar senha
  const PasswordToggle = ({ show, toggle }: { show: boolean, toggle: () => void }) => (
    <Box
      as="button"
      aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
      onClick={toggle}
      color="gray.500"
      _hover={{ color: "#3333CC" }}
      height="24px"
      width="24px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="transparent"
      border="none"
      outline="none"
      cursor="pointer"
      p={0}
    >
      {show ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" 
            fill="currentColor"/>
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.8 17.5 12 17.5C8.2 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C10.62 9.5 9.5 10.62 9.5 12C9.5 13.38 10.62 14.5 12 14.5C13.38 14.5 14.5 13.38 14.5 12C14.5 10.62 13.38 9.5 12 9.5ZM12 7.5C14.48 7.5 16.5 9.52 16.5 12C16.5 14.48 14.48 16.5 12 16.5C9.52 16.5 7.5 14.48 7.5 12C7.5 9.52 9.52 7.5 12 7.5Z" 
            fill="currentColor" />
          <path d="M3.71 2.29L2.29 3.71L20.29 21.71L21.71 20.29L3.71 2.29Z" fill="currentColor" />
        </svg>
      )}
    </Box>
  );

  return (
    <Box minH="100vh" bg="white" display="flex">
      {/* Lado Esquerdo - Ilustração */}
      <Box
        flex="1"
        bg="linear-gradient(135deg, #3333CC 0%, #2828A0 100%)"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
      >
        {/* Logo BS2 */}
        <Box position="absolute" top="40px" left="40px" zIndex={2}>
          <Image 
            src="/banco-bs2-logo-branco-2.png" 
            alt="BS2 Seguros" 
            width="150px" 
            height="auto" 
          />
        </Box>

        {/* Elementos decorativos de fundo */}
        <Box
          position="absolute"
          top="20%"
          right="-10%"
          width="300px"
          height="300px"
          borderRadius="full"
          bg="rgba(255,255,255,0.1)"
          zIndex={1}
        />
        <Box
          position="absolute"
          bottom="10%"
          left="-5%"
          width="200px"
          height="200px"
          borderRadius="full"
          bg="rgba(255,255,255,0.05)"
          zIndex={1}
        />

        {/* Imagem principal */}
        <Box position="relative" zIndex={2} maxW="600px" width="100%" px={8}>
          <Image
            src="/login-illustration.svg"
            alt="Ilustração de segurança e proteção"
            width="100%"
            height="auto"
            objectFit="contain"
          />
        </Box>
      </Box>

      {/* Lado Direito - Formulário de Alteração de Senha */}
      <Box
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="white"
        px={{ base: 6, md: 12 }}
      >
        <Box width="100%" maxW="400px">
          <VStack gap={8} align="stretch">
            {/* Título */}
            <Box textAlign="center">
              <Heading size="xl" color="#1A1A1A" mb={2}>
                Alterar Senha
              </Heading>
              <Text color="gray.600">
                {reason || 'É necessário alterar sua senha para continuar'}
              </Text>
            </Box>

            {/* Formulário */}
            <VStack gap={6}>
              {/* Campo de Login */}
              <Box width="100%">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Login
                </Text>
                <Input
                  placeholder="Digite seu login"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  borderColor="#E2E8F0"
                  _hover={{ borderColor: "#3333CC" }}
                  _focus={{ borderColor: "#3333CC", boxShadow: "0 0 0 1px #3333CC" }}
                  readOnly={!!location.search.includes('username')}
                />
              </Box>

              {/* Campo de Senha Atual */}
              <Box width="100%" position="relative">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Senha Atual
                </Text>
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha atual"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  borderColor="#E2E8F0"
                  _hover={{ borderColor: "#3333CC" }}
                  _focus={{ borderColor: "#3333CC", boxShadow: "0 0 0 1px #3333CC" }}
                  pr="50px"
                />
                <Box 
                  position="absolute" 
                  right="12px" 
                  top="calc(50% + 10px)" 
                  transform="translateY(-50%)" 
                  zIndex={2}
                  height="24px"
                  display="flex"
                  alignItems="center"
                >
                  <PasswordToggle 
                    show={showCurrentPassword} 
                    toggle={() => setShowCurrentPassword(!showCurrentPassword)} 
                  />
                </Box>
              </Box>

              {/* Campo de Nova Senha */}
              <Box width="100%" position="relative">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Nova Senha
                </Text>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Digite sua nova senha"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  borderColor="#E2E8F0"
                  _hover={{ borderColor: "#3333CC" }}
                  _focus={{ borderColor: "#3333CC", boxShadow: "0 0 0 1px #3333CC" }}
                  pr="50px"
                />
                <Box 
                  position="absolute" 
                  right="12px" 
                  top="calc(50% + 10px)" 
                  transform="translateY(-50%)" 
                  zIndex={2}
                  height="24px"
                  display="flex"
                  alignItems="center"
                >
                  <PasswordToggle 
                    show={showNewPassword} 
                    toggle={() => setShowNewPassword(!showNewPassword)} 
                  />
                </Box>
              </Box>
              {/* Fora do Box acima: lá dentro empurraria o ícone de mostrar senha, posicionado pela altura */}
              <Text width="100%" color="gray.500" fontSize="xs" mt={-4}>{PASSWORD_RULES}</Text>

              {/* Campo de Confirmação de Nova Senha */}
              <Box width="100%" position="relative">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Confirme a Nova Senha
                </Text>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirme sua nova senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  borderColor={confirmPassword !== '' && newPassword !== confirmPassword ? "red.500" : "#E2E8F0"}
                  _hover={{ borderColor: confirmPassword !== '' && newPassword !== confirmPassword ? "red.500" : "#3333CC" }}
                  _focus={{ borderColor: confirmPassword !== '' && newPassword !== confirmPassword ? "red.500" : "#3333CC", boxShadow: `0 0 0 1px ${confirmPassword !== '' && newPassword !== confirmPassword ? "red.500" : "#3333CC"}` }}
                  pr="50px"
                />
                <Box 
                  position="absolute" 
                  right="12px" 
                  top="calc(50% + 10px)" 
                  transform="translateY(-50%)" 
                  zIndex={2}
                  height="24px"
                  display="flex"
                  alignItems="center"
                >
                  <PasswordToggle 
                    show={showConfirmPassword} 
                    toggle={() => setShowConfirmPassword(!showConfirmPassword)} 
                  />
                </Box>
                {confirmPassword !== '' && newPassword !== confirmPassword && (
                  <Text color="red.500" fontSize="sm" mt={1}>As senhas não coincidem</Text>
                )}
              </Box>

              {/* Mensagem de erro */}
              {error && (
                <Box bg="red.50" p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500">
                  <Text color="red.600" fontSize="sm">{error}</Text>
                </Box>
              )}
              
              {/* Botão de Alteração de Senha */}
              <Button
                bg="#3333CC"
                color="white"
                size="lg"
                width="100%"
                borderRadius="md"
                onClick={handleChangePassword}
                _hover={{ bg: "#2828A0" }}
                _active={{ bg: "#1F1F80" }}
                boxShadow="0 4px 12px rgba(51,51,204,0.3)"
                fontWeight="bold"
                fontSize="md"
                disabled={isLoading}
              >
                {isLoading ? 'Alterando senha...' : 'Alterar Senha'}
              </Button>
              
              {/* Botão para voltar ao login */}
              <Button
                variant="ghost"
                size="md"
                width="100%"
                onClick={() => navigate('/login')}
                color="gray.600"
                _hover={{ bg: "gray.100" }}
              >
                Voltar para o login
              </Button>
            </VStack>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};
